import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { parse, format } from 'date-fns';
import React, { memo, useEffect, useMemo, useState } from 'react';
import { EngagementPlanTask } from '@redux/tasks/tasksTypes';
import { Button } from 'components/Button/Button';
import { ExpirySelectBlock } from 'components/ExpirySelectBlock/ExpirySelectBlock';
import { FREQUENCY_SELECT_OPTIONS, FrequencySelectBlock } from 'components/FrequencySelectBlock/FrequencySelectBlock';
import { InfoLabel } from 'components/InfoLabel/InfoLabel';
import { PlanTaskDescriptionString } from 'components/PlanTaskDescriptionString/PlanTaskDescriptionString';
import { TextArea } from 'components/TextArea/TextArea';
import { TriggerSelectBlock, TriggerTypes } from 'components/TriggerSelectBlock/TriggerSelectBlock';
import { Text } from 'components/Typography';
import { isTimeDuringSleep } from 'utils/form';
import { MAX_TASK_MESSAGE_LENGTH } from '../../../../configConsts';
import { customDateTimePickerStyles } from '../../../../customStyles';
import style from './TaskEditArea.scss';

type Props = {
  task: EngagementPlanTask;
  duration: number;
  onSaveUpdatedTask: (updatedTask: EngagementPlanTask) => void;
};

type UpdatedTaskOptionsProps = EngagementPlanTask & {
  deliveryStartTimeDate: Date;
  deliveryExpirationTimeDate: Date;
  hasValidTimeFormat: boolean;
};

export const TaskEditArea: React.FC<Props> = memo(({ task, duration, onSaveUpdatedTask }) => {
  const [updatedTaskOptions, setUpdatedTaskOptions] = useState<UpdatedTaskOptionsProps | null>(null);

  const errors = useMemo(() => {
    return updatedTaskOptions
      ? {
          timeFormat: updatedTaskOptions?.hasValidTimeFormat ? '' : 'Please select a valid time',
          dateTime: isTimeDuringSleep(updatedTaskOptions?.deliveryStartTimeDate) ? 'Time must be between 5AM and 12AM' : '',
          expiredDateTime: updatedTaskOptions?.deliveryExpirationTimeDate ? '' : 'Please select an expiration time'
        }
      : { message: '', timeFormat: '', dateTime: '', expiredDateTime: '' };
  }, [updatedTaskOptions]);

  const isValidForm = !Object.values(errors).filter(Boolean).length;

  useEffect(() => {
    !!task &&
      setUpdatedTaskOptions({
        ...task,
        triggerCondition: task.triggerCondition || TriggerTypes.inBed,
        deliveryStartTimeDate: parse(task.deliveryStartTime, 'HH:mm:ss', new Date()),
        deliveryExpirationTimeDate: parse(task.deliveryExpirationTime, 'HH:mm:ss', new Date()),
        hasValidTimeFormat: true
      });
  }, [task]);

  if (!updatedTaskOptions) {
    return null;
  }

  return (
    <>
      <div className={style.container}>
        <div className={style.scrollableContent}>
          <div className={style.header}>
            <Text variant="h5" weight={500}>
              {updatedTaskOptions.taskName}
            </Text>
            <PlanTaskDescriptionString task={updatedTaskOptions} duration={duration} />
          </div>
          {!!task?.deliverableMessage && (
            <>
              <InfoLabel text="You can type in your own words — Luna polishes the rest." />
              <TextArea
                rows={4}
                maxLength={MAX_TASK_MESSAGE_LENGTH}
                value={updatedTaskOptions?.deliverableMessage}
                onChange={(e) =>
                  setUpdatedTaskOptions((prev) => ({
                    ...prev!,
                    deliverableMessage: e.target.value
                  }))
                }
              />
            </>
          )}

          <div className={style.multiConditionLine}>
            <div className={style.conditionHolder}>
              <Text variant="nlg" className={style.conditionTitle}>
                Deliver at
              </Text>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <TimePicker
                  value={updatedTaskOptions.deliveryStartTimeDate}
                  onChange={(date) => {
                    if (date?.toString() === 'Invalid Date') {
                      setUpdatedTaskOptions((prev) => ({
                        ...prev!,
                        hasValidTimeFormat: false
                      }));
                      return;
                    }

                    if (date) {
                      setUpdatedTaskOptions((prev) => ({
                        ...prev!,
                        deliveryStartTimeDate: date,
                        deliveryStartTime: format(date, 'HH:mm:ss'),
                        hasValidTimeFormat: true
                      }));
                    }
                  }}
                  shouldDisableTime={(timeValue) => isTimeDuringSleep(timeValue)}
                  sx={customDateTimePickerStyles}
                />
              </LocalizationProvider>
              {!!errors.timeFormat && (
                <Text variant="sm" className={style.error}>
                  {errors.timeFormat}
                </Text>
              )}
              {!errors.timeFormat && !!errors.dateTime && (
                <Text variant="sm" className={style.error}>
                  {errors.dateTime}
                </Text>
              )}
            </div>
            <div className={style.conditionHolder}>
              <ExpirySelectBlock
                selectedTime={updatedTaskOptions.deliveryStartTimeDate}
                expiredTime={updatedTaskOptions.deliveryExpirationTimeDate}
                onExpiredTimeSelected={(date) => {
                  if (date) {
                    setUpdatedTaskOptions((prev) => ({
                      ...prev!,
                      deliveryExpirationTimeDate: date as Date,
                      deliveryExpirationTime: format(date as Date, 'HH:mm:ss')
                    }));
                  }
                }}
                error={errors.expiredDateTime}
              />
            </div>
          </div>

          <TriggerSelectBlock
            trigger={updatedTaskOptions.triggerCondition as TriggerTypes}
            onTriggerChanged={(value) =>
              setUpdatedTaskOptions((prev) => ({
                ...prev!,
                triggerCondition: value
              }))
            }
          />

          <FrequencySelectBlock
            frequency={{ repeat: updatedTaskOptions.frequencyType as FREQUENCY_SELECT_OPTIONS }}
            allowedFrequencyTypes={[FREQUENCY_SELECT_OPTIONS.every_day, FREQUENCY_SELECT_OPTIONS.no_repeat]}
            showFrequencyDetails={false}
            onFrequencyChanged={(value: any) =>
              setUpdatedTaskOptions((prev) => ({
                ...prev!,
                frequencyType: value.repeat,
                endsAfterOccurrences: value.repeat === FREQUENCY_SELECT_OPTIONS.every_day ? duration : 1
              }))
            }
            errors={{}}
          />
        </div>
      </div>
      <div className={style.drawerFooter}>
        <Button variant="contained" onClick={() => onSaveUpdatedTask(updatedTaskOptions)} className={style.continueButton} disabled={!isValidForm}>
          Save
        </Button>
      </div>
    </>
  );
});
